import { Box, Link as MuiLink, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { neutral } from '@ahora-patagonia/design-tokens';
import type { NotaPublicaCard } from '@ahora-patagonia/contracts';
import { formatearFecha } from '../formato/fecha.js';
import { ImagenNota } from '../media/ImagenNota.js';
import { SIZES } from '../media/srcset.js';

export interface TarjetaNotaProps {
  nota: NotaPublicaCard;
  /** Variante tipográfica del titular (la posición manda, no la tarjeta). */
  variante?: 'titularTarjeta' | 'titularPortada23';
  sizes?: string;
  /** Sin clamp y con bajada: SOLO la posición 1 de la portada usa esto en otro componente. */
  conBajada?: boolean;
  /**
   * Posición del ranking, sobre la foto. **Solo "Más leídas"**: en cualquier otro bloque el orden
   * no significa nada y un número al frente sería decorar con un dato que no existe.
   */
  numero?: number;
}

/**
 * Tarjeta con foto 16:9 arriba (plantillas B y D). El titular corta en **3 líneas** con elipsis
 * (regla de §4).
 *
 * **Sin volanta de categoría.** Esta tarjeta siempre se dibuja dentro de un bloque que ya dice de
 * qué sección es —el rótulo "Tecnología" está arriba, con su "Ver todo"—, así que repetirlo en cada
 * nota no agrega nada. Y era peor que redundante: la volanta salía de `categories[0]`, de modo que
 * una nota clasificada en seis categorías mostraba "TURISMO" dentro del bloque de Tecnología. La
 * categoría se nombra **solo en la portada**, que es el único bloque que mezcla secciones a mano y
 * donde el lector no tiene otra forma de saber de dónde viene cada nota (ver `Portada.tsx`).
 */
export function TarjetaNota({
  nota,
  variante = 'titularTarjeta',
  sizes = SIZES.tarjeta,
  conBajada = false,
  numero,
}: TarjetaNotaProps) {
  return (
    <Box component="article">
      <MuiLink
        component={RouterLink}
        to={nota.path}
        sx={{ display: 'block', position: 'relative' }}
      >
        <ImagenNota imagen={nota.cover} ratio="16:9" sizes={sizes} />
        {numero !== undefined && (
          <Typography
            component="span"
            // El número lo anuncia la lista ordenada que envuelve al bloque, no cada tarjeta: si
            // lo leyera también acá, el lector de pantalla diría la posición dos veces.
            aria-hidden="true"
            sx={{
              position: 'absolute',
              left: 0,
              bottom: 0,
              fontWeight: 800,
              fontSize: 28,
              lineHeight: 1,
              px: 1,
              py: 0.25,
              color: '#fff',
              // Ground propio y no `--color-cliente` a secas: el número se apoya sobre una foto
              // cualquiera, y sin fondo hay medios cuyo color queda ilegible sobre una foto clara.
              backgroundColor: 'var(--color-cliente)',
            }}
          >
            {numero}
          </Typography>
        )}
      </MuiLink>
      <Box sx={{ pt: 1 }}>
        <MuiLink component={RouterLink} to={nota.path}>
          <Typography
            variant={variante}
            component="h3"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {nota.title}
          </Typography>
        </MuiLink>
        {conBajada && nota.excerpt && (
          <Typography
            variant="bajada"
            component="p"
            sx={{
              mt: 0.5,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {nota.excerpt}
          </Typography>
        )}
        <Typography variant="firma" component="p" sx={{ color: neutral[700], mt: 0.5 }}>
          {nota.author} · {formatearFecha(nota.publishedAt)}
        </Typography>
      </Box>
    </Box>
  );
}
